import React from "react";
import { Compass } from "lucide-react";
import NewsletterCTA from "../components/NewsletterCTA";

const GRAIN_MACRO =
  "https://customer-assets.emergentagent.com/job_web-instructions-3/artifacts/fr19ijh5_Shutterstock_2293226913.webp";
const FIELD_IMG =
  "https://customer-assets.emergentagent.com/job_web-instructions-3/artifacts/g0tkyaa4_pexels-deep-malik-132129520-10738421.webp";

const TYPES = [
  { name: "Pearl (Bajra)", note: "The workhorse. Iron-rich, drought-proof." },
  { name: "Finger (Ragi)", note: "The calcium king. More than dairy." },
  { name: "Foxtail", note: "Mild, fast-cooking, rice-replacer." },
  { name: "Proso", note: "Short-season, brain-friendly lecithin." },
  { name: "Barnyard", note: "45-day grower, low-cal, high-fiber." },
  { name: "Kodo", note: "Nutty, antioxidant powerhouse." },
  { name: "Little", note: "Tiny grains, couscous-like." },
  { name: "Browntop", note: "The rarest. Revived by smallholders." },
  { name: "Sorghum (Jowar)", note: "Gluten-free flour hero." },
];

const BENEFITS = [
  { title: "Low glycemic", body: "Most millets sit at 50-60 GI — half of white rice." },
  { title: "High iron", body: "Pearl millet delivers ~8mg Fe per 100g." },
  { title: "Gut-friendly", body: "Resistant starch feeds your microbiome." },
  { title: "Gluten-free", body: "No celiac worry. No cross-contamination." },
  { title: "Real protein", body: "11-13g protein per 100g, complete profile." },
  { title: "Water-smart", body: "Uses ~12x less water than almonds per litre of milk." },
];

// Simple SVG continent silhouettes for the timeline
const ChinaSVG = () => (
  <svg viewBox="0 0 100 100" className="h-16 w-16 md:h-20 md:w-20" fill="currentColor">
    <path d="M15 40 Q10 50 18 62 L25 72 Q32 80 45 78 L60 75 Q75 70 82 58 L88 48 Q85 35 75 30 L60 22 Q45 18 30 25 L20 30 Q15 35 15 40Z" />
  </svg>
);
const IndiaSVG = () => (
  <svg viewBox="0 0 100 100" className="h-16 w-16 md:h-20 md:w-20" fill="currentColor">
    <path d="M35 15 L55 15 Q62 18 65 25 L72 40 Q75 55 68 65 L60 80 Q55 90 50 88 L45 85 Q40 75 38 65 L35 55 Q30 45 32 35 Q33 25 35 15Z" />
  </svg>
);
const AfricaSVG = () => (
  <svg viewBox="0 0 100 100" className="h-16 w-16 md:h-20 md:w-20" fill="currentColor">
    <path d="M40 10 L58 10 Q68 12 72 22 L75 35 Q78 48 72 60 L65 72 Q55 85 50 88 Q45 85 40 78 L35 65 Q28 52 30 40 L32 28 Q35 18 40 10Z" />
  </svg>
);
const NorthAmericaSVG = () => (
  <svg viewBox="0 0 100 100" className="h-16 w-16 md:h-20 md:w-20" fill="currentColor">
    <path d="M10 25 Q8 15 22 12 L45 8 Q62 6 72 14 L82 22 Q88 32 82 45 L75 58 Q70 68 58 72 L46 74 Q38 72 30 66 L22 60 Q14 52 12 42 Q10 34 10 25Z" />
  </svg>
);

const TIMELINE = [
  {
    era: "7000 BCE",
    place: "China",
    body:
      "Foxtail & proso millet domesticated along the Yellow River. Fed the first dynasties long before rice dominated the plate.",
    Icon: ChinaSVG,
    bg: "bg-butter",
    fg: "text-jet",
    iconColor: "text-cobalt",
  },
  {
    era: "3000 BCE",
    place: "India",
    body:
      "Pearl & finger millet planted across the Deccan plateau. Became the daily grain of Rajasthan, Karnataka and Tamil Nadu — and still is.",
    Icon: IndiaSVG,
    bg: "bg-cobalt",
    fg: "text-cream",
    iconColor: "text-butter",
  },
  {
    era: "2500 BCE",
    place: "Africa",
    body:
      "Pearl millet domesticated in the Sahel. Carried across West Africa as the staple that thrived where nothing else would grow.",
    Icon: AfricaSVG,
    bg: "bg-butter",
    fg: "text-jet",
    iconColor: "text-cobalt",
  },
  {
    era: "2025 AD",
    place: "USA & Canada",
    body:
      "Where it's just starting to matter. Prairie farmers are scaling proso millet, and a new generation of brands (hi) is putting it in cartons, snack bars and coffee shops.",
    Icon: NorthAmericaSVG,
    bg: "bg-orange",
    fg: "text-cream",
    iconColor: "text-butter",
    isLatest: true,
  },
];

export default function WhatIsMillet() {
  return (
    <main data-testid="page-what-is-millet">
      {/* Hero */}
      <section className="grid border-b-2 border-jet bg-cream pt-[104px] md:pt-[128px] lg:grid-cols-5">
        <div className="border-b-2 border-jet p-8 md:p-14 lg:col-span-3 lg:border-b-0 lg:border-r-2 lg:p-20">
          <p className="font-sans text-xs font-extrabold uppercase tracking-[0.22em] text-cobalt">
            Field Guide
          </p>
          <h1 className="mt-4 font-sans text-6xl font-extrabold leading-[0.98] tracking-tight text-jet md:text-8xl lg:text-[7rem]">
            The <span className="text-cobalt">OG</span> <br />
            Supergrain.
          </h1>
          <p className="mt-8 max-w-2xl font-sans text-lg font-semibold leading-snug text-jet md:text-xl">
            A family of small, drought-hardy grains that fed humans for 10,000
            years before anyone needed to call them a &ldquo;superfood&rdquo;.
          </p>
        </div>
        <div className="relative overflow-hidden bg-gradient-to-br from-sun-orange via-sun-red to-sun-yellow lg:col-span-2">
          <img
            src={GRAIN_MACRO}
            alt="Millet macro"
            className="relative h-full w-full object-cover mix-blend-multiply"
          />
        </div>
      </section>

      {/* Quick stats */}
      <section className="border-b-2 border-jet bg-surface p-8 md:p-14 lg:p-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { n: "10,000+", l: "Years of cultivation" },
            { n: "9", l: "Varieties in the family" },
            { n: "~12×", l: "Less water" },
          ].map((s) => (
            <div key={s.l} className="rounded-[2rem] border-2 border-jet bg-cream p-8 shadow-pop-lg">
              <p className="font-sans text-6xl font-extrabold leading-none tracking-tight text-cobalt md:text-7xl">
                {s.n}
              </p>
              <p className="mt-3 font-sans text-xs font-extrabold uppercase tracking-[0.22em]">
                {s.l}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Types */}
      <section data-testid="section-types" className="border-b-2 border-jet bg-cream p-8 md:p-14 lg:p-20">
        <p className="font-sans text-xs font-extrabold uppercase tracking-[0.22em] text-cobalt">§ Types</p>
        <h2 className="mt-3 font-sans text-5xl font-extrabold leading-[1.02] tracking-tight md:text-7xl">
          The nine you should know.
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {TYPES.map((t, i) => (
            <div
              key={t.name}
              data-testid={`type-card-${i}`}
              className="rounded-[2rem] border-2 border-jet bg-surface p-6 transition-transform hover:-translate-y-1 hover:bg-butter"
            >
              <p className="font-sans text-xs font-extrabold uppercase tracking-[0.22em] text-cobalt">0{i + 1}</p>
              <h3 className="mt-2 font-sans text-2xl font-extrabold tracking-tight">{t.name}</h3>
              <p className="mt-2 font-sans text-sm text-jet/80">{t.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits — cobalt */}
      <section data-testid="section-benefits" className="relative overflow-hidden border-b-2 border-jet bg-cobalt p-8 text-cream md:p-14 lg:p-20">
        <p className="font-sans text-xs font-extrabold uppercase tracking-[0.22em] text-butter">§ Benefits</p>
        <h2 className="mt-3 font-sans text-5xl font-extrabold leading-[1.02] tracking-tight md:text-7xl">
          What it does inside you.
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <div key={b.title} className="rounded-[2rem] border-2 border-cream bg-cream/5 p-6 backdrop-blur">
              <p className="font-sans text-xs font-extrabold uppercase tracking-[0.22em] text-butter">0{i + 1}</p>
              <h3 className="mt-2 font-sans text-2xl font-extrabold tracking-tight">{b.title}</h3>
              <p className="mt-2 font-sans text-sm text-cream/85 md:text-base">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHERE IT GROWS — TIMELINE with maps */}
      <section
        data-testid="section-timeline"
        className="relative overflow-hidden border-b-2 border-jet bg-cream p-8 md:p-14 lg:p-20"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-jet bg-orange text-cream">
            <Compass size={18} />
          </span>
          <p className="font-sans text-xs font-extrabold uppercase tracking-[0.22em] text-cobalt">§ Where it grows</p>
        </div>
        <h2 className="mt-4 font-sans text-5xl font-extrabold leading-[1.02] tracking-tight md:text-7xl">
          A 10,000 year{" "}
          <span className="relative inline-block">
            road trip.
            <span className="absolute -bottom-2 left-0 h-3 w-full rounded-full bg-butter"></span>
          </span>
        </h2>
        <p className="mt-6 max-w-2xl font-sans text-lg font-semibold text-jet md:text-xl">
          Millet didn't start on a supermarket shelf. It crossed three
          continents before wheat, corn, or oats were even domesticated.
        </p>

        {/* Timeline — flat 4-column row with a single curved dashed road weaving through all 4 phase discs */}
        <div className="relative mt-14">
          {/* Desktop */}
          <div className="relative hidden md:block">
            {/* Curved connecting road — undulates through the center of each disc */}
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[160px] w-full"
              viewBox="0 0 1000 160"
              preserveAspectRatio="none"
            >
              <path
                d="M 0 80 Q 62 10 125 80 T 375 80 T 625 80 T 875 80 T 1000 80"
                fill="none"
                stroke="#0B0B0F"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="10 10"
                vectorEffect="non-scaling-stroke"
              />
              {/* end-of-road arrowhead after the last disc */}
              <path
                d="M 980 68 L 996 80 L 980 92"
                fill="none"
                stroke="#0B0B0F"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            <div className="relative grid grid-cols-4 gap-6">
              {TIMELINE.map((t) => (
                <div
                  key={t.place}
                  className="flex flex-col items-center text-center"
                >
                  <div className="relative z-10 inline-flex h-[148px] w-[148px] items-center justify-center rounded-full border-[3px] border-jet bg-cream shadow-pop-lg">
                    <div
                      className={`flex h-[128px] w-[128px] items-center justify-center rounded-full border-2 border-jet ${t.bg} ${t.fg}`}
                    >
                      <div className={t.iconColor}>
                        <t.Icon />
                      </div>
                    </div>
                    {t.isLatest && (
                      <span className="absolute -right-2 -top-2 rotate-[12deg] rounded-full border-2 border-jet bg-butter px-2.5 py-1 font-sans text-[10px] font-black uppercase tracking-[0.15em] text-jet">
                        now
                      </span>
                    )}
                  </div>
                  <span
                    className="sticker mt-4"
                    style={{ background: "#0B0B0F", color: "#F5EDDC", transform: "rotate(-3deg)" }}
                  >
                    {t.era}
                  </span>
                  <h3 className="mt-3 font-sans text-2xl font-extrabold tracking-tight md:text-3xl">
                    {t.place}
                  </h3>
                  <p className="mt-2 max-w-[260px] font-sans text-sm leading-relaxed text-jet/85 md:text-base">
                    {t.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile */}
          <div className="relative md:hidden">
            <div className="absolute left-[44px] top-0 bottom-0 w-0 border-l-[4px] border-dashed border-jet" />
            <div className="flex flex-col gap-10">
              {TIMELINE.map((t) => (
                <div key={t.place} className="relative flex items-start gap-5">
                  <div className="relative z-10 inline-flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-full border-[3px] border-jet bg-cream shadow-pop-lg">
                    <div className={`flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 border-jet ${t.bg} ${t.fg}`}>
                      <div className={t.iconColor}>
                        <t.Icon />
                      </div>
                    </div>
                  </div>
                  <div className="pt-2">
                    <span
                      className="sticker"
                      style={{ background: "#0B0B0F", color: "#F5EDDC", transform: "rotate(-3deg)" }}
                    >
                      {t.era}
                    </span>
                    <h3 className="mt-2 font-sans text-2xl font-extrabold tracking-tight">
                      {t.place}
                      {t.isLatest && (
                        <span className="ml-2 rounded-full border-2 border-jet bg-butter px-2 py-0.5 font-sans text-[10px] font-black uppercase tracking-[0.15em] text-jet">
                          now
                        </span>
                      )}
                    </h3>
                    <p className="mt-2 font-sans text-sm leading-relaxed text-jet/85">
                      {t.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter — shared orange section */}
      <NewsletterCTA source="what-is-millet" id="newsletter" />
    </main>
  );
}
